/**
 * Compare-window maths for the app-wide date range (SellerMate W0.3).
 *
 * The header's date control reads "current ▸ compared to". This module owns
 * the second half: given the window a page is actually showing, which window
 * does it compare against? Pure functions over local `YYYY-MM-DD` calendar
 * dates (lib/format/date.ts rule 1 — never `toISOString`), so every case is
 * tested without a DOM or a clock (dateRangeCompare.test.ts).
 *
 *   - none        no comparison.
 *   - previous    the same number of days, ending the day before the current
 *                 window starts ("Last 30 days" ▸ the 30 days before that).
 *   - last_year   the same calendar dates one year earlier. Feb 29 becomes
 *                 Feb 28, because the earlier year has no Feb 29.
 *   - custom      two dates the operator typed. Reversed ends are swapped; a
 *                 missing or impossible date means no comparison yet.
 *
 * The compare window is always derived from the window the page FETCHES (the
 * pick, on PPC pages after the move back to
 * the last complete report day), so both halves describe the same length of
 * the same kind of data.
 */
import { addDaysYMD, diffDaysYMD, fmtMonthDay, isoYMD, parseYMD } from './format/date'

export type CompareMode = 'none' | 'previous' | 'last_year' | 'custom'

/** Every mode, in the order the header lists them. */
export const COMPARE_MODES: readonly CompareMode[] = ['none', 'previous', 'last_year', 'custom']

/** Plain-English name of each mode, as the header shows it. */
export const COMPARE_LABELS: Record<CompareMode, string> = {
  none: 'No comparison',
  previous: 'Previous period',
  last_year: 'Same period last year',
  custom: 'Custom dates',
}

/** An inclusive run of local calendar days, `YYYY-MM-DD`. */
export type DateWindow = { from: string; to: string }

/** What the operator chose to compare against. `from`/`to` are read only for
 *  'custom' (they are the typed dates). */
export type CompareSetting = { mode: CompareMode; from?: string; to?: string }

export const DEFAULT_COMPARE: CompareSetting = { mode: 'none' }

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/

/** True for a real calendar date written `YYYY-MM-DD`. `2026-02-31` is false:
 *  `Date` would quietly roll it over to March 3. */
export function isValidYMD(value: unknown): value is string {
  if (typeof value !== 'string' || !YMD_RE.test(value)) return false
  const d = parseYMD(value)
  return d !== null && isoYMD(d.getFullYear(), d.getMonth(), d.getDate()) === value
}

/** Days in a window, counting both ends (a single day is 1). Never below 1, so
 *  a malformed window cannot produce an empty or inverted comparison. */
export function windowDays(w: DateWindow): number {
  return Math.max(1, diffDaysYMD(w.from, w.to) + 1)
}

/** The same number of days, ending the day before `w` starts. */
export function previousPeriod(w: DateWindow): DateWindow {
  return { from: addDaysYMD(w.from, -windowDays(w)), to: addDaysYMD(w.from, -1) }
}

/** The same month and day `years` from `ymd` (negative = earlier). A day the
 *  target month does not have is pinned to its last day (Feb 29 → Feb 28). */
export function shiftYears(ymd: string, years: number): string {
  const d = parseYMD(ymd)
  if (!d) return ymd
  const y = d.getFullYear() + years
  const m0 = d.getMonth()
  const lastDay = new Date(y, m0 + 1, 0).getDate()
  return isoYMD(y, m0, Math.min(d.getDate(), lastDay))
}

/** The same calendar dates one year earlier. */
export function samePeriodLastYear(w: DateWindow): DateWindow {
  return { from: shiftYears(w.from, -1), to: shiftYears(w.to, -1) }
}

/** Two typed dates as a window: both must be real dates; reversed ends are
 *  swapped. `null` means "not usable yet" (the header asks for dates). */
export function customWindow(from: unknown, to: unknown): DateWindow | null {
  if (!isValidYMD(from) || !isValidYMD(to)) return null
  return from <= to ? { from, to } : { from: to, to: from }
}

/** The window `current` is compared against, or `null` for no comparison. */
export function compareWindow(current: DateWindow, setting: CompareSetting): DateWindow | null {
  switch (setting.mode) {
    case 'previous': return previousPeriod(current)
    case 'last_year': return samePeriodLastYear(current)
    case 'custom': return customWindow(setting.from, setting.to)
    default: return null
  }
}

/** The comparison a strip of period tiles draws (the Campaigns tiles): each
 *  tile against its OWN previous period or its own dates last year. A custom
 *  comparison is one window — it fits the page's range, not every tile — so
 *  the tiles then show none. */
export function perPeriodCompareMode(setting: CompareSetting): 'none' | 'previous' | 'last_year' {
  return setting.mode === 'previous' || setting.mode === 'last_year' ? setting.mode : 'none'
}


/** A stored compare setting, validated. Anything unrecognised (a hand-edited
 *  entry, a newer build's mode) reads as no comparison rather than throwing. */
export function parseCompareSetting(raw: unknown): CompareSetting {
  if (!raw || typeof raw !== 'object') return DEFAULT_COMPARE
  const r = raw as Record<string, unknown>
  const mode = COMPARE_MODES.find(m => m === r.mode)
  if (!mode) return DEFAULT_COMPARE
  if (mode !== 'custom') return { mode }
  return {
    mode,
    from: isValidYMD(r.from) ? r.from : undefined,
    to: isValidYMD(r.to) ? r.to : undefined,
  }
}

/** "Aug 24 – Sep 22"; one day reads "Sep 22". The year is added when the
 *  window is not in `thisYear` — a same-period-last-year window would
 *  otherwise print exactly like the current one. */
export function fmtWindow(w: DateWindow, thisYear: number): string {
  const fy = Number(w.from.slice(0, 4))
  const ty = Number(w.to.slice(0, 4))
  const from = fmtMonthDay(w.from)
  const to = fmtMonthDay(w.to)
  if (fy !== ty) return `${from}, ${fy} – ${to}, ${ty}`
  const body = w.from === w.to ? from : `${from} – ${to}`
  return fy === thisYear ? body : `${body}, ${fy}`
}

/** The header's one-line summary: "Aug 24 – Sep 22 ▸ Jul 25 – Aug 23". With no
 *  comparison window, the right half says why: "No comparison", or "Pick
 *  compare dates" while a custom comparison is still missing its dates. */
export function rangeSummary(
  current: DateWindow, compare: DateWindow | null, mode: CompareMode, thisYear: number,
  /** False on a page that draws no comparison: the window alone. */
  showCompare = true,
): string {
  if (!showCompare) return fmtWindow(current, thisYear)
  const right = compare
    ? fmtWindow(compare, thisYear)
    : mode === 'custom' ? 'Pick compare dates' : COMPARE_LABELS.none
  return `${fmtWindow(current, thisYear)} ▸ ${right}`
}
